import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { baseApiURL } from "../baseUrl";
import {
  FiImage, FiVideo, FiFileText, FiLink, FiX, FiTrash2, FiEdit2,
  FiThumbsUp, FiMessageCircle, FiShare2, FiMoreHorizontal,
  FiGlobe, FiClock, FiSend, FiPlus, FiDownload, FiExternalLink,
  FiMaximize2, FiCalendar
} from "react-icons/fi";
import { FaLinkedin, FaRegSmile } from "react-icons/fa";

/* ── Time ago helper ─────────────────────────────────────────── */
const timeAgo = (dateStr) => {
  if (!dateStr) return "Just now";
  const now = new Date();
  const past = new Date(dateStr);
  const diffSec = Math.floor((now - past) / 1000);
  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return past.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const daysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return 30;
  const diff = new Date(expiresAt) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

/* ── Lightbox Image Modal ────────────────────────────────────── */
const ImageModal = ({ src, alt, onClose }) => {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white hover:text-indigo-400 text-xl transition cursor-pointer"
        >
          <FiX />
        </button>
        <img src={src} alt={alt || "Notice attachment"} className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" />
      </div>
    </div>
  );
};

/* ── Post Media Grid Component ───────────────────────────────── */
const PostMedia = ({ media, onOpenImage }) => {
  if (!media || media.length === 0) return null;

  const images = media.filter(m => m.resourceType === "image" || m.mimeType?.startsWith("image/"));
  const videos = media.filter(m => m.resourceType === "video" || m.mimeType?.startsWith("video/"));
  const docs = media.filter(m => m.resourceType === "raw" || (!m.mimeType?.startsWith("image/") && !m.mimeType?.startsWith("video/")));

  return (
    <div className="mt-3 space-y-2">
      {/* Images Grid */}
      {images.length > 0 && (
        <div className={`grid gap-1.5 rounded-xl overflow-hidden ${
          images.length === 1 ? "grid-cols-1" : images.length === 2 ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"
        }`}>
          {images.map((img, i) => (
            <div
              key={i}
              className="relative group cursor-pointer bg-slate-950 overflow-hidden"
              onClick={() => onOpenImage(img.url)}
            >
              <img
                src={img.url}
                alt={img.originalName || "Notice Image"}
                className={`w-full object-cover transition duration-300 group-hover:scale-105 ${
                  images.length === 1 ? "max-h-[480px]" : "h-56"
                }`}
              />
              <div className="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                <FiMaximize2 size={20} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.map((vid, i) => (
        <div key={i} className="rounded-xl overflow-hidden bg-slate-950 border border-slate-200 dark:border-slate-800">
          <video src={vid.url} controls className="w-full max-h-[450px] object-contain" />
        </div>
      ))}

      {/* Document attachments */}
      {docs.map((doc, i) => (
        <a
          key={i}
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="flex items-center gap-3 p-3.5 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-indigo-500/50 transition group"
        >
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 rounded-lg group-hover:bg-indigo-600 group-hover:text-white transition">
            <FiFileText size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
              {doc.originalName || "Attached Document / Notice PDF"}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">Click to view or download file</p>
          </div>
          <div className="p-2 text-slate-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition">
            <FiDownload size={16} />
          </div>
        </a>
      ))}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
const Notice = () => {
  const router = useLocation();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Expanded comment sections map { noticeId: true/false }
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});

  // Expanded descriptions map
  const [expandedDesc, setExpandedDesc] = useState({});

  // Composer Form
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "both",
    link: "",
  });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const currentUserRole = router.pathname.replace("/", "") || "student";
  const currentUserId = localStorage.getItem("loginid") || localStorage.getItem("enrollmentNo") || "user";
  const isAdmin = currentUserRole === "admin" || currentUserRole === "faculty";

  /* ── Fetch Notices ─────────────────────────────────────────── */
  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      let types = ["both"];
      if (currentUserRole === "student") types = ["student", "both"];
      else if (currentUserRole === "faculty") types = ["faculty", "both", "student"];
      else types = ["student", "both", "faculty"];

      const res = await axios.get(`${baseApiURL()}/notice/getNotice`, {
        params: { type: types },
      });
      if (res.data.success) {
        setNotices(res.data.notice || []);
      }
    } catch {
      toast.error("Failed to load notice feed");
    } finally {
      setLoading(false);
    }
  }, [currentUserRole]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  /* ── Media Selection ───────────────────────────────────────── */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (mediaFiles.length + files.length > 5) {
      toast.error("Maximum 5 attachments per post");
      return;
    }
    const newItems = files.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      type: file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "raw",
      name: file.name,
      size: (file.size / (1024 * 1024)).toFixed(1),
    }));
    setMediaFiles((prev) => [...prev, ...newItems]);
    e.target.value = "";
  };

  const removeMedia = (idx) => {
    setMediaFiles((prev) => {
      const copy = [...prev];
      if (copy[idx].preview) URL.revokeObjectURL(copy[idx].preview);
      copy.splice(idx, 1);
      return copy;
    });
  };

  const resetComposer = () => {
    setFormData({ title: "", description: "", type: "both", link: "" });
    mediaFiles.forEach((m) => m.preview && URL.revokeObjectURL(m.preview));
    setMediaFiles([]);
    setEditingNotice(null);
    setComposerOpen(false);
  };

  const openEdit = (notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title || "",
      description: notice.description || "",
      type: notice.type || "both",
      link: notice.link || "",
    });
    setMediaFiles([]);
    setComposerOpen(true);
  };

  /* ── Submit Post ───────────────────────────────────────────── */
  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error("Title and description are required");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading(editingNotice ? "Updating notice..." : "Publishing to Notice Feed...");

    try {
      if (editingNotice) {
        const res = await axios.put(`${baseApiURL()}/notice/updateNotice/${editingNotice._id}`, {
          title: formData.title,
          description: formData.description,
          type: formData.type,
          link: formData.link,
        });
        toast.dismiss(toastId);
        if (res.data.success) {
          toast.success("Post updated!");
          fetchNotices();
          resetComposer();
        }
      } else {
        const fd = new FormData();
        fd.append("title", formData.title);
        fd.append("description", formData.description);
        fd.append("type", formData.type);
        if (formData.link) fd.append("link", formData.link);
        fd.append("postedBy", isAdmin ? "Sphoorthy Administration" : "Campus Notice");
        fd.append("postedRole", "Official Campus Announcement");

        mediaFiles.forEach((m) => fd.append("media", m.file));

        const res = await axios.post(`${baseApiURL()}/notice/addNotice`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        toast.dismiss(toastId);
        if (res.data.success) {
          toast.success("🎉 Published to Campus Feed!");
          fetchNotices();
          resetComposer();
        }
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.message || "Failed to publish notice");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Delete Post ───────────────────────────────────────────── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notice post and all its cloud media?")) return;
    const tid = toast.loading("Deleting post...");
    try {
      const res = await axios.delete(`${baseApiURL()}/notice/deleteNotice/${id}`);
      toast.dismiss(tid);
      if (res.data.success) {
        toast.success("Post removed");
        setNotices((prev) => prev.filter((n) => n._id !== id));
      }
    } catch {
      toast.dismiss(tid);
      toast.error("Failed to delete post");
    }
  };

  /* ── Like / React ──────────────────────────────────────────── */
  const handleToggleLike = async (noticeId) => {
    try {
      const res = await axios.post(`${baseApiURL()}/notice/like/${noticeId}`, {
        userId: currentUserId,
      });
      if (res.data.success) {
        setNotices((prev) =>
          prev.map((n) => (n._id === noticeId ? { ...n, likes: res.data.likes } : n))
        );
      }
    } catch {
      toast.error("Error updating reaction");
    }
  };

  /* ── Add Comment ───────────────────────────────────────────── */
  const handleAddComment = async (noticeId) => {
    const text = commentInputs[noticeId];
    if (!text || !text.trim()) return;

    try {
      const res = await axios.post(`${baseApiURL()}/notice/comment/${noticeId}`, {
        text: text.trim(),
        author: localStorage.getItem("userName") || currentUserId,
        authorRole: currentUserRole === "student" ? "Student" : "Faculty",
      });
      if (res.data.success) {
        setNotices((prev) =>
          prev.map((n) => (n._id === noticeId ? { ...n, comments: res.data.comments } : n))
        );
        setCommentInputs((prev) => ({ ...prev, [noticeId]: "" }));
        toast.success("Comment added");
      }
    } catch {
      toast.error("Failed to add comment");
    }
  };

  const handleShare = (notice) => {
    const url = window.location.href;
    navigator.clipboard.writeText(`${notice.title} - Sphoorthy Notice: ${url}`);
    toast.success("Notice link copied to clipboard!");
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* ── Lightbox Image Modal ── */}
      <ImageModal src={lightboxUrl} onClose={() => setLightboxUrl(null)} />

      {/* ── LINKEDIN: "START A POST" BOX ───────────────────────── */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-indigo-600 via-indigo-500 to-cyan-500 p-0.5 shrink-0 shadow-sm">
            <div className="w-full h-full bg-white dark:bg-slate-900 rounded-full flex items-center justify-center font-black text-indigo-600 dark:text-indigo-400 text-xs">
              {isAdmin ? "ADM" : "STU"}
            </div>
          </div>
          <button
            onClick={() => {
              setEditingNotice(null);
              setFormData({ title: "", description: "", type: "both", link: "" });
              setMediaFiles([]);
              setComposerOpen(true);
            }}
            className="flex-1 text-left px-4 py-3 bg-slate-100 dark:bg-slate-950 hover:bg-slate-200/80 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs md:text-sm font-medium rounded-full border border-slate-200 dark:border-slate-800 transition cursor-pointer"
          >
            Start a post, announcement, or notice...
          </button>
        </div>

        {/* Action Buttons Row */}
        <div className="flex items-center justify-around mt-3 pt-3 border-t border-slate-100 dark:border-slate-800/80 text-xs font-semibold text-slate-600 dark:text-slate-400">
          <button
            onClick={() => {
              setComposerOpen(true);
              setTimeout(() => fileInputRef.current?.click(), 100);
            }}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition cursor-pointer text-emerald-600 dark:text-emerald-400"
          >
            <FiImage className="text-lg text-emerald-500" />
            <span className="text-slate-700 dark:text-slate-300">Photo</span>
          </button>
          <button
            onClick={() => {
              setComposerOpen(true);
              setTimeout(() => fileInputRef.current?.click(), 100);
            }}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition cursor-pointer text-indigo-600 dark:text-indigo-400"
          >
            <FiVideo className="text-lg text-indigo-500" />
            <span className="text-slate-700 dark:text-slate-300">Video</span>
          </button>
          <button
            onClick={() => {
              setComposerOpen(true);
              setTimeout(() => fileInputRef.current?.click(), 100);
            }}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition cursor-pointer text-amber-600 dark:text-amber-400"
          >
            <FiFileText className="text-lg text-amber-500" />
            <span className="text-slate-700 dark:text-slate-300">Document</span>
          </button>
          <button
            onClick={() => setComposerOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition cursor-pointer text-rose-600 dark:text-rose-400"
          >
            <FiLink className="text-lg text-rose-500" />
            <span className="text-slate-700 dark:text-slate-300">Article / Link</span>
          </button>
        </div>
      </div>

      {/* ── LINKEDIN: CREATE / EDIT POST MODAL ─────────────────── */}
      {composerOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 w-full max-w-xl rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <FaLinkedin className="text-indigo-600 text-xl" />
                <h2 className="text-sm font-bold text-slate-900 dark:text-white">
                  {editingNotice ? "Edit Notice Post" : "Create a Notice Post"}
                </h2>
              </div>
              <button onClick={resetComposer} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer text-slate-500">
                <FiX size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmitPost} className="p-5 overflow-y-auto space-y-4 flex-1">
              {/* Author & Audience Pill */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-indigo-600 to-cyan-500 flex items-center justify-center font-bold text-white text-xs shadow-xs shrink-0">
                  {isAdmin ? "SEC" : "STU"}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-white">
                    {isAdmin ? "Sphoorthy Administration" : "Campus Notice"}
                  </p>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="mt-0.5 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-[10px] font-bold text-slate-700 dark:text-slate-300 rounded-full border border-slate-200 dark:border-slate-700 outline-none"
                  >
                    <option value="both">🌐 Anyone (Students + Faculty)</option>
                    <option value="student">🎓 Students Only</option>
                    <option value="faculty">👨‍🏫 Faculty Only</option>
                  </select>
                </div>
              </div>

              {/* Title Input */}
              <div>
                <input
                  type="text"
                  required
                  placeholder="Notice Title / Headline *"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Description Body */}
              <div>
                <textarea
                  required
                  rows={6}
                  placeholder="What do you want to announce? Share details, guidelines, or instructions..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none leading-relaxed"
                />
              </div>

              {/* Optional Link */}
              <div>
                <input
                  type="url"
                  placeholder="Attach Link / URL (e.g. https://...)"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Selected Media Attachments Previews */}
              {!editingNotice && mediaFiles.length > 0 && (
                <div className="grid grid-cols-2 gap-2 pt-2">
                  {mediaFiles.map((m, i) => (
                    <div key={i} className="relative group bg-slate-100 dark:bg-slate-950 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
                      {m.type === "image" && m.preview ? (
                        <img src={m.preview} alt="" className="w-full h-28 object-cover" />
                      ) : (
                        <div className="w-full h-28 flex flex-col items-center justify-center p-3 text-center">
                          {m.type === "video" ? <FiVideo size={24} className="text-indigo-500 mb-1" /> : <FiFileText size={24} className="text-amber-500 mb-1" />}
                          <p className="text-[10px] font-bold text-slate-800 dark:text-slate-200 truncate w-full">{m.name}</p>
                          <p className="text-[9px] text-slate-400">{m.size} MB</p>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeMedia(i)}
                        className="absolute top-1.5 right-1.5 p-1 bg-rose-600 text-white rounded-full shadow-md hover:bg-rose-500 transition cursor-pointer"
                      >
                        <FiX size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Modal Footer / Action Toolbar */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                {!editingNotice && (
                  <div className="flex items-center gap-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 rounded-lg transition cursor-pointer"
                      title="Add Media (Photos / Videos / Files)"
                    >
                      <FiImage size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 rounded-lg transition cursor-pointer"
                      title="Add Video"
                    >
                      <FiVideo size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-indigo-600 rounded-lg transition cursor-pointer"
                      title="Add Document / PDF"
                    >
                      <FiFileText size={18} />
                    </button>
                    <span className="text-[10px] text-slate-400 ml-2">Stored on Cloudinary • Auto-expires in 30d</span>
                  </div>
                )}

                <div className="flex items-center gap-2 ml-auto">
                  <button
                    type="button"
                    onClick={resetComposer}
                    className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <FiSend size={13} />
                    <span>{editingNotice ? "Save" : "Post"}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── LINKEDIN FEED: POSTS STREAM ────────────────────────── */}
      {loading ? (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
          <div className="inline-block animate-spin rounded-full h-7 w-7 border-2 border-indigo-600 border-t-transparent mb-2" />
          <p className="text-xs font-semibold text-slate-400">Loading campus feed...</p>
        </div>
      ) : notices.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs space-y-2">
          <FaLinkedin className="mx-auto text-4xl text-slate-300 dark:text-slate-700" />
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">No Notice Posts Yet</h3>
          <p className="text-xs text-slate-400 max-w-xs mx-auto">Be the first to start a post or campus announcement.</p>
        </div>
      ) : (
        notices.map((notice) => {
          const isLiked = notice.likes?.includes(currentUserId);
          const daysLeft = daysUntilExpiry(notice.expiresAt);
          const isExpanded = expandedDesc[notice._id];
          const hasLongDesc = notice.description?.length > 220;
          const showComments = expandedComments[notice._id];

          return (
            <div
              key={notice._id}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs hover:shadow-md transition overflow-hidden"
            >
              {/* Card Header (Author Info) */}
              <div className="p-4 pb-2 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-indigo-600 to-cyan-500 p-0.5 shrink-0 shadow-sm">
                    <div className="w-full h-full bg-white dark:bg-slate-900 rounded-full flex items-center justify-center font-black text-indigo-600 dark:text-indigo-400 text-xs">
                      SEC
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-xs font-bold text-slate-900 dark:text-white">
                        {notice.postedBy || "Sphoorthy Engineering College"}
                      </h3>
                      <span className="text-indigo-600 dark:text-indigo-400 text-[10px]">✓</span>
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1">
                      <span>{notice.postedRole || "Official Campus Notice"}</span>
                      <span>•</span>
                      <span>{timeAgo(notice.createdAt)}</span>
                      <span>•</span>
                      <FiGlobe size={9} title="Public Notice" />
                    </p>
                  </div>
                </div>

                {/* Top-Right Badges & Admin Actions */}
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border uppercase tracking-wider ${
                    notice.type === 'student' ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800' :
                    notice.type === 'faculty' ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800' :
                    'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                  }`}>
                    {notice.type}
                  </span>

                  {isAdmin && (
                    <div className="flex items-center gap-1 ml-1">
                      <button
                        onClick={() => openEdit(notice)}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer"
                        title="Edit Post"
                      >
                        <FiEdit2 size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(notice._id)}
                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer"
                        title="Delete Post"
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Post Title & Text */}
              <div className="px-4 py-2 space-y-1.5">
                <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-snug">
                  {notice.title}
                </h2>
                <div className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                  {isExpanded || !hasLongDesc
                    ? notice.description
                    : `${notice.description.slice(0, 220)}...`}
                  {hasLongDesc && (
                    <button
                      onClick={() => setExpandedDesc((prev) => ({ ...prev, [notice._id]: !isExpanded }))}
                      className="ml-1 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-bold text-xs cursor-pointer inline-block"
                    >
                      {isExpanded ? "see less" : "…see more"}
                    </button>
                  )}
                </div>

                {notice.link && (
                  <div className="pt-1">
                    <a
                      href={notice.link.startsWith("http") ? notice.link : `https://${notice.link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-semibold hover:bg-indigo-100 transition"
                    >
                      <FiExternalLink size={12} />
                      <span>{notice.link}</span>
                    </a>
                  </div>
                )}
              </div>

              {/* Media Attachments Box (Images / Videos / Files) */}
              <div className="px-4">
                <PostMedia media={notice.media} onOpenImage={(url) => setLightboxUrl(url)} />
              </div>

              {/* Engagement Stats Row */}
              <div className="px-4 pt-3 pb-1 flex items-center justify-between text-[11px] text-slate-400 border-b border-slate-100 dark:border-slate-800/80">
                <div className="flex items-center gap-1.5">
                  <span className="p-1 bg-indigo-600 text-white rounded-full text-[9px] shadow-xs">
                    <FiThumbsUp size={9} />
                  </span>
                  <span>{notice.likes?.length || 0} reactions</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setExpandedComments((prev) => ({ ...prev, [notice._id]: !showComments }))}
                    className="hover:underline cursor-pointer"
                  >
                    {notice.comments?.length || 0} comments
                  </button>
                  <span>•</span>
                  <span className="flex items-center gap-1 text-[10px] text-slate-400">
                    <FiClock size={10} /> Expires in {daysLeft}d
                  </span>
                </div>
              </div>

              {/* LinkedIn Interaction Toolbar (Like, Comment, Share, Save) */}
              <div className="px-2 py-1 flex items-center justify-around text-xs font-bold text-slate-600 dark:text-slate-400">
                <button
                  onClick={() => handleToggleLike(notice._id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
                    isLiked ? "text-indigo-600 dark:text-indigo-400" : ""
                  }`}
                >
                  <FiThumbsUp size={15} className={isLiked ? "fill-current" : ""} />
                  <span>Like</span>
                </button>
                <button
                  onClick={() => setExpandedComments((prev) => ({ ...prev, [notice._id]: !showComments }))}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/60 transition cursor-pointer"
                >
                  <FiMessageCircle size={15} />
                  <span>Comment</span>
                </button>
                <button
                  onClick={() => handleShare(notice)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/60 transition cursor-pointer"
                >
                  <FiShare2 size={15} />
                  <span>Share</span>
                </button>
              </div>

              {/* Comment Section (Expandable) */}
              {showComments && (
                <div className="p-4 bg-slate-50 dark:bg-slate-950/60 border-t border-slate-100 dark:border-slate-800 space-y-3">
                  {/* Add Comment Input */}
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                      U
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={commentInputs[notice._id] || ""}
                        onChange={(e) =>
                          setCommentInputs((prev) => ({ ...prev, [notice._id]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddComment(notice._id);
                        }}
                        className="w-full pl-3.5 pr-10 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        onClick={() => handleAddComment(notice._id)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-indigo-600 hover:text-indigo-500 rounded-full cursor-pointer"
                      >
                        <FiSend size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Previous Comments List */}
                  {notice.comments && notice.comments.length > 0 && (
                    <div className="space-y-2 pt-2">
                      {notice.comments.map((c, ci) => (
                        <div key={ci} className="flex items-start gap-2.5 text-xs">
                          <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                            {c.author?.[0] || "U"}
                          </div>
                          <div className="flex-1 bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-slate-900 dark:text-white">{c.author}</span>
                              <span className="text-[9px] text-slate-400">{timeAgo(c.createdAt)}</span>
                            </div>
                            <p className="text-slate-700 dark:text-slate-300 mt-0.5">{c.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default Notice;
